import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, TransparentCard } from "@/components/ui/card";
import { Check, Clock, Target, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageWithNavbar from "./PageWithNavbar";

interface RoadmapItem {
  title: string;
  description: string;
  status: "completed" | "experimental" | "development" | "planned";
  category: "core" | "premium" | "premium-only" | "enterprise" | "subscription";
  quarter?: string;
}

const roadmapItems: RoadmapItem[] = [
  // Completed Features
  {
    title: "U.S Exchanges",
    description: "Support for all major U.S exchanges -- Coinbase, Kraken, Crypto.com, Gemini and BinanceUS -- with full trading capabilities.",
    status: "completed",
    category: "core",
  },
  {
    title: "Account Management",
    description: "Real-time portfolio tracking across all connected exchanges. View all of your balances across exchanges in one place. Filterable balance sheet for quick asset overview.",
    status: "completed",
    category: "core",
  },
  {
    title: "Local, Private, Encrypted",
    description: "All of your exchange data is stored on your computer and encrypted. We do not view, verify or store anything related to access of your accounts on our servers.",
    status: "completed",
    category: "core",
  },
  {
    title: "Anonymous Crypto Payments",
    description: "Bitcoin and Monero payment support with auto-generated anonymous accounts for privacy-focused users.",
    status: "completed",
    category: "premium-only",
  },
  {
    title: "AI-Powered News Feed",
    description: "Access to real-time market news and analysis powered by AI. Gain insight into market trends through analysis of each article's sentiment, and gain it faster with our generated article summary.",
    status: "completed",
    category: "premium",
  },
  {
    title: "White-label Solutions",
    description: "Customizable platform for institutional clients and trading firms. Includes branding, custom features, and dedicated support.",
    status: "completed",
    category: "enterprise",
  },
  {
    title: "Order Management System",
    description: "Professional-grade order placement and execution. Hotkey-enabled for high-performance trading. Filterable open and historical order views as well as trade history.",
    status: "completed",
    category: "core",
  },
  {
    title: "Advanced Depth of Book",
    description: "Most advanced and customizable depth of book available for real-time order flow analysis. Adjust the number of levels, visible exchanges, and condense levels to find your edge.",
    status: "completed",
    category: "core",
  },
  {
    title: "Time & Sales",
    description: "Professional-grade time and sales data for real-time liquidity analysis. View and filter where liquidity is in real-time across all exchanges.",
    status: "completed",
    category: "core",
  },
  {
    title: "Live & Email Support",
    description: "Currently our team is a small upstart band of traders, but we are working to expand our support team to provide 24/7 coverage.",
    status: "completed",
    category: "premium"
  },
  
  // Experimental Features
  {
    title: "Scripting Engine: Indicators (Python)",
    description: "Unrestricted Python scripting for custom indicators. We include the following indicators out of the box: SMA, RSA, EMA, MACD, Bollinger Bands and VWAP. What will you build?",
    status: "experimental",
    category: "core",
  },
  {
    title: "Scripting Engine: Algorithms (Python)",
    description: "Write scripts utilizing the full power of Python for algorithmic trading and order execution. Whether it's a GARCH model in numpy, a forecast model from huggingface, or a custom indicator-based strategy all your own.",
    status: "experimental",
    category: "premium",
  },
  {
    title: "Multi-chart Support",
    description: "Support for multiple charts simultaneously.",
    status: "experimental",
    category: "premium",
  },
  
  // Development Features
  {
    title: "nonkyc.io",
    description: "Support for the nonkyc.io exchange.",
    status: "development",
    category: "core",
  },
  {
    title: "General News Events",
    description: "Support for data releases and news articles not directly related to cryptocurrency, including macroeconomic events and geopolitical developments that may impact market conditions.",
    status: "development",
    category: "premium",
  },
  {
    title: "Account Management+",
    description: "Enhanced account management system that includes cost-basis tracking, coin analysis (market cap, float, supply), risk analysis tools and news relevant to your portfolio. All packaged into a clean, built-in dashboard for easy access.",
    status: "development",
    category: "core",
  },
  {
    title: "Backtest Engine",
    description: "A comprehensive backtesting framework for testing trading strategies against historical data.",
    status: "development",
    category: "premium",
  },
  {
    title: "Linux Support",
    description: "Support for Linux operating systems, including installation and compatibility improvements.",
    status: "development",
    category: "core",
  },
  
  // Planned Features
  {
    title: "Advanced Charting",
    description: "Enhanced charting suite for advanced technical analysis. Support for multi-chart layouts, tick charts and TPO charts.",
    status: "planned",
    category: "premium",
  },
  {
    title: "Direct-to-Venue Instructions",
    description: "Detail the Drasil binary protocol to let users talk directly to the Venue without gRPC and intermediary application overhead.",
    status: "planned",
    category: "premium",
  },
  {
    title: "Scripting Engine: Order Types",
    description: "Unlock unique execution strategies with Python scripting for custom order types. We include the following order types and modifiers out of the box: Limit, Stop, Stop-Limit and Post Only.",
    status: "planned",
    category: "premium",
  },
  {
    title: "Sandbox Mode",
    description: "A safe environment for testing and developing trading strategies and experimenting with features without risking real funds.",
    status: "planned",
    category: "core",
  },
  {
    title: "International Exchanges",
    description: "Support for international exchanges with full trading capabilities: Binance, Gate.io, Bitget, OKX and MEXC",
    status: "planned",
    category: "core",
  },
  {
    title: "Scripting Engine: Plugins",
    description: "Extend the functionality of the Doyen platform with custom plugins. Alongside indicators, order types and trading strategies, implement custom data visualization and filtering tools - the possibilities are endless.",
    status: "planned",
    category: "core",
  },
  {
    title: "Scripting Engine: Crossfeed API",
    description: "Make use of all available data that Doyen processes across indicators, algorithms, and plugins.",
    status: "planned",
    category: "premium",
  },
  {
    title: "FIX Support",
    description: "FIX protocol support for a more powerful HFT trading experience. Available on select exchanges.",
    status: "planned",
    category: "premium",
  },
  {
    title: "Mobile App",
    description: "A mobile application for trading on the go, with a user-friendly interface and all the features of the desktop version.",
    status: "planned",
    category: "core",
  },
];

const getStatusIcon = (status: RoadmapItem['status']) => {
  switch (status) {
    case 'completed':
      return <Check className="h-5 w-5 text-green-500" />;
    case 'experimental':
      return <Zap className="h-5 w-5 text-yellow-500" />;
    case 'development':
      return <Zap className="h-5 w-5 text-purple-500" />;
    case 'planned':
      return <Clock className="h-5 w-5 text-blue-500" />;
  }
};

const getStatusBadge = (status: RoadmapItem['status']) => {
  switch (status) {
    case 'completed':
      return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">✅ Available (Complete)</Badge>;
    case 'experimental':
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">⚡ Available (In-Progress)</Badge>;
    case 'development':
      return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">✨ Unavailable (In-Progress)</Badge>;
    case 'planned':
      return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">🎯 Unavailable (Planned)</Badge>;
  }
};

const getCategoryBadge = (category: RoadmapItem['category']) => {
  switch (category) {
    case 'core':
      return <Badge variant="outline">Core</Badge>;
    case 'premium':
      return <Badge variant="default">Premium & Enterprise</Badge>;
    case 'premium-only':
      return <Badge variant="default">Premium Only</Badge>;
    case 'enterprise':
      return <Badge variant="default">Enterprise Only</Badge>;
    case 'subscription':
      return <Badge variant="outline">Subscription</Badge>;
  }
};

export const Roadmap = () => {
  const navigate = useNavigate();

  const completedFeatures = roadmapItems.filter(item => item.status === 'completed');
  const experimentalFeatures = roadmapItems.filter(item => item.status === 'experimental');
  const developmentFeatures = roadmapItems.filter(item => item.status === 'development');
  const plannedFeatures = roadmapItems.filter(item => item.status === 'planned');

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            Doyen Trading{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              Roadmap
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Track our progress toward the complete cryptocurrency trading mastery platform.
            Contact us with suggestions, feedback, or questions at <a href="mailto:support@doyentrading.com" className="text-primary underline">contact@doyentrading.com</a>
          </p>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold">Road to 1.0</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{completedFeatures.length}</div>
                <div className="text-sm text-muted-foreground">Completed Features</div>
              </div>
              <div className="bg-yellow-100 dark:bg-yellow-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{experimentalFeatures.length + developmentFeatures.length}</div>
                <div className="text-sm text-muted-foreground">In Development</div>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{plannedFeatures.length}</div>
                <div className="text-sm text-muted-foreground">Planned Features</div>
              </div>
            </div>
          </div>
        </div>

        {/* Completed Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Check className="h-6 w-6 text-green-500" />
            Completed Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedFeatures.map((item, index) => (
              <Card key={index} className="border-green-200 dark:border-green-800">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(item.status)}
                    {getCategoryBadge(item.category)}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* In Progress Features */}
        <section className="mb-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="h-6 w-6 text-yellow-500 mb-2" />
              Experimental Features
            </h2>
            <p>Features currently available that we're seeking feedback on.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {experimentalFeatures.map((item, index) => (
              <Card key={index} className="border-yellow-200 dark:border-yellow-800">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                    </div>
                    {item.quarter && (
                      <Badge variant="outline" className="text-xs">
                        {item.quarter}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(item.status)}
                    {getCategoryBadge(item.category)}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Development Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Clock className="h-6 w-6 text-purple-500" />
            In Development
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {developmentFeatures.map((item, index) => (
              <Card key={index} className="border-blue-200 dark:border-purple-800">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                    </div>
                    {item.quarter && (
                      <Badge variant="outline" className="text-xs">
                        {item.quarter}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(item.status)}
                    {getCategoryBadge(item.category)}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>        
        </section>
        
        {/* Planned Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-500" />
            Planned
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plannedFeatures.map((item, index) => (
              <Card key={index} className="border-blue-200 dark:border-blue-800">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                    </div>
                    {item.quarter && (
                      <Badge variant="outline" className="text-xs">
                        {item.quarter}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(item.status)}
                    {getCategoryBadge(item.category)}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>        
        </section>

        {/* Bug Reporting Section 
        <section className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Help Us Improve</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Found a bug or have a feature request? Your feedback helps us prioritize development and deliver a better trading experience.
            </p>
          </div>
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-xl font-semibold mb-2">Bug Tracker & Feature Requests</h3>
                  <p className="text-muted-foreground">
                    Report bugs, vote on existing issues, and submit feature requests to help shape the future of Doyen Trading.
                  </p>
                </div>
                <Button 
                  onClick={() => navigate("/bug-tracker")}
                  className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                  size="lg"
                >
                  Visit Bug Tracker
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
        */}
        {/* Call to Action */}
        <section className="text-center">
          <TransparentCard className="bg-muted/50 p-8">
            <h3 className="text-2xl font-bold mb-4">
              Start Trading Today
            </h3>
            <p className="text-muted-foreground mb-6">
              Get immediate access to professional-grade cryptocurrency trading tools. 
              Join thousands of traders already using Doyen to enhance their trading performance.
            </p>            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/")}
                className="bg-primary hover:bg-primary/90"
              >
                View Pricing Plans
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/setup")}
              >
                Exchange Setup
              </Button>
              {/*
              <Button 
                variant="outline"
                onClick={() => navigate("/bug-tracker")}
              >
                Report Bugs & Issues
              </Button>
              */}
              <Button 
                variant="outline"
                onClick={() => navigate("/")}
              >
                Learn More About Doyen
              </Button>
            </div>
          </TransparentCard>
        </section>
      </div>
    </div>
    </PageWithNavbar>
  );
};
